import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Save, Loader2, RotateCcw, AlertTriangle, ShieldCheck, User as UserIcon, Info, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithStepUp } from './StepUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Settings → System → Access time
 *
 * Lets the operator change the JWT TTL for admin and customer
 * sessions. Backed by `app_config.session_policy` via
 * GET/PUT /api/admin/security/session-policy.
 *
 * Renders two segmented controls (Days / Hours / Minutes) per role
 * plus a numeric input so the operator can be precise or pick a
 * common preset (e.g. "3 days" for admin, "365 days" for customer).
 */
export default function AccessTimeEditor({ token }) {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [policy, setPolicy]     = useState(null); // {admin_minutes, customer_minutes, limits, defaults}
  // Local editor state — minutes per role.
  const [adminMin, setAdminMin]       = useState(0);
  const [customerMin, setCustomerMin] = useState(0);
  // Phase F — Admin MFA roster (per-admin Exempt toggle). Loaded
  // alongside the session policy so the panel only has one spinner.
  const [admins, setAdmins]     = useState(null);
  const [mfaBusy, setMfaBusy]   = useState(null); // email currently being toggled

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policyResp, overviewResp] = await Promise.all([
        fetch(`${API_URL}/api/admin/security/session-policy`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/security/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!policyResp.ok) throw new Error(`policy HTTP ${policyResp.status}`);
      const j = await policyResp.json();
      setPolicy(j);
      setAdminMin(Number(j.admin_minutes || 0));
      setCustomerMin(Number(j.customer_minutes || 0));
      // overview is best-effort — admin roster is auxiliary, don't
      // block the session-policy editor if /overview returns 403/500
      if (overviewResp.ok) {
        const ov = await overviewResp.json();
        setAdmins(Array.isArray(ov?.admin_mfa) ? ov.admin_mfa : []);
      } else {
        setAdmins([]);
      }
    } catch (e) {
      toast.error(`Failed to load access time policy: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/session-policy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          admin_minutes: Number(adminMin),
          customer_minutes: Number(customerMin),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast.success(
        `Saved — admin sessions ${prettyDuration(j.admin_minutes)}, customer ${prettyDuration(j.customer_minutes)}. ` +
        'New limits apply on the next login.',
        { duration: 8000 },
      );
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!policy?.defaults) return;
    setAdminMin(policy.defaults.admin_minutes);
    setCustomerMin(policy.defaults.customer_minutes);
  };

  // Toggle MFA requirement for a single admin. Server is superadmin-gated
  // and step-up-guarded; we surface failures as toasts so non-superadmin
  // attempts get a clear "MFA exemption requires superadmin" message.
  const toggleAdminExempt = async (admin, nextExempt) => {
    const msg = nextExempt
      ? `Disable MFA requirement for ${admin.email}?\n\nThe admin will log in with email + password only. Existing MFA secret + backup codes will be wiped. You can re-enable MFA for this account at any time.`
      : `Re-enable MFA requirement for ${admin.email}?\n\nOn next login they will be forced to enrol a fresh authenticator.`;
    if (!window.confirm(msg)) return;
    setMfaBusy(admin.email);
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/auth/mfa/admin-set-exempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target_user_id: admin.id, exempt: nextExempt }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast.success(nextExempt
        ? `MFA disabled for ${admin.email}.`
        : `MFA requirement restored for ${admin.email}.`);
      await load();
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    } finally {
      setMfaBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading session policy…
      </div>
    );
  }
  if (!policy) return null;

  const dirty =
    adminMin !== policy.admin_minutes ||
    customerMin !== policy.customer_minutes;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="access-time-editor">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Access time</h2>
          <p className="text-sm text-slate-600 mt-0.5">
            How long a login lasts before re-authentication is required.
            Changes take effect on the <strong>next login</strong> — existing
            sessions keep their current expiry.
          </p>
        </div>
      </header>

      {/* Admin role card */}
      <RoleCard
        title="Admin sessions"
        subtitle="JWT TTL for accounts with role=admin (you, ops, recovery admin)."
        icon={ShieldCheck}
        accent="from-rose-500 to-amber-500"
        minutes={adminMin}
        setMinutes={setAdminMin}
        min={policy.limits.admin_min}
        max={policy.limits.admin_max}
        defaultVal={policy.defaults.admin_minutes}
        presets={[
          { label: '15 min',  value: 15 },
          { label: '8 h',     value: 60 * 8 },
          { label: '24 h',    value: 60 * 24 },
          { label: '3 days',  value: 60 * 24 * 3 },
          { label: '7 days',  value: 60 * 24 * 7 },
          { label: '30 days', value: 60 * 24 * 30 },
        ]}
        dataTestId="admin"
      />

      {/* Customer role card */}
      <RoleCard
        title="Customer sessions"
        subtitle="JWT TTL for accounts with role=user (your paying customers)."
        icon={UserIcon}
        accent="from-cyan-500 to-emerald-500"
        minutes={customerMin}
        setMinutes={setCustomerMin}
        min={policy.limits.customer_min}
        max={policy.limits.customer_max}
        defaultVal={policy.defaults.customer_minutes}
        presets={[
          { label: '1 h',     value: 60 },
          { label: '24 h',    value: 60 * 24 },
          { label: '7 days',  value: 60 * 24 * 7 },
          { label: '30 days', value: 60 * 24 * 30 },
          { label: '90 days', value: 60 * 24 * 90 },
          { label: '365 days', value: 60 * 24 * 365 },
        ]}
        dataTestId="customer"
      />

      {/* Phase F — Admin MFA requirement per account. Lives next to
          the session-policy controls because both are "who can log
          in and how" knobs and our 2-admin team manages them in the
          same workflow. Only superadmins can flip the toggle
          server-side; non-superadmins still see the row but get a
          403 toast on click. */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="admin-mfa-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-800">Admin MFA requirement</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Toggle whether each admin must complete a 6-digit authenticator code
              on login. When OFF, the admin logs in with email + password only and
              their MFA setup is wiped.
              <span className="ml-1 text-amber-700 font-medium">Superadmin only.</span>
            </p>
          </div>
        </div>

        {(!admins || admins.length === 0) ? (
          <div className="text-xs text-slate-500 italic px-1 py-2">
            No admin accounts visible. (Need superadmin role to load the roster.)
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-slate-500 border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">Email</th>
                <th className="text-left py-1.5 pr-3">Tier</th>
                <th className="text-left py-1.5 pr-3">MFA setup</th>
                <th className="text-left py-1.5 pr-3">Required?</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b last:border-0" data-testid={`admin-mfa-row-${a.email}`}>
                  <td className="py-2 pr-3 font-medium">{a.email}</td>
                  <td className="py-2 pr-3 text-slate-600">{a.admin_tier || '—'}</td>
                  <td className="py-2 pr-3">
                    {a.mfa_enabled
                      ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> enrolled</span>
                      : <span className="text-slate-500 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> not enrolled</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => toggleAdminExempt(a, !a.mfa_exempt)}
                      disabled={mfaBusy === a.email || !a.is_active}
                      data-testid={`mfa-exempt-toggle-${a.email}`}
                      title={a.mfa_exempt
                        ? 'MFA bypassed — click to require again'
                        : 'MFA required — click to disable for this admin'}
                      className={
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border transition disabled:opacity-50 ' +
                        (a.mfa_exempt
                          ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                          : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100')
                      }
                    >
                      {mfaBusy === a.email ? '…' : (a.mfa_exempt ? 'OFF (bypassed)' : 'ON (required)')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — info + actions */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Security events (password reset, role change, MFA reset, account
          deactivation) invalidate <em>every</em> existing session for that
          user regardless of these caps. Lengthening TTLs only affects how
          long a normal, uninterrupted session stays alive.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={resetDefaults}
          disabled={saving}
          data-testid="access-time-reset-defaults"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reset to defaults
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="access-time-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function RoleCard({
  title, subtitle, icon: Icon, accent,
  minutes, setMinutes,
  min, max, defaultVal,
  presets, dataTestId,
}) {
  const outOfRange = minutes < min || minutes > max;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center shrink-0`}>
          <Icon className="w-4.5 h-4.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">
          <div data-testid={`access-time-${dataTestId}-current`} className="font-mono text-sm text-slate-800">
            {prettyDuration(minutes)}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            current
          </div>
        </div>
      </div>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`access-time-${dataTestId}-presets`}>
        {presets.map((p) => (
          <button
            key={p.value}
            onClick={() => setMinutes(p.value)}
            data-testid={`access-time-${dataTestId}-preset-${p.value}`}
            className={
              'px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ' +
              (minutes === p.value
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom minutes input */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Custom (in minutes)
          </label>
          <input
            type="number"
            value={minutes}
            min={min}
            max={max}
            step={1}
            onChange={(e) => setMinutes(Math.max(0, Number(e.target.value) || 0))}
            data-testid={`access-time-${dataTestId}-input`}
            className={
              'w-full h-10 px-3 rounded-lg border bg-white text-slate-800 font-mono text-sm outline-none ' +
              (outOfRange
                ? 'border-rose-400 focus:border-rose-500'
                : 'border-slate-300 focus:border-indigo-500')
            }
          />
        </div>
        <div className="text-[11px] text-slate-500 pb-2.5 leading-tight">
          Min: {prettyDuration(min)}<br />
          Max: {prettyDuration(max)}<br />
          Default: {prettyDuration(defaultVal)}
        </div>
      </div>

      {outOfRange && (
        <div className="mt-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Value out of allowed range ({prettyDuration(min)} – {prettyDuration(max)}).
            It will be clamped to the nearest bound on save.
          </span>
        </div>
      )}
    </div>
  );
}

function prettyDuration(minutes) {
  if (!minutes || minutes <= 0) return '0 min';
  const m = Number(minutes);
  if (m < 60) return `${m} min`;
  if (m < 60 * 24) {
    const h = m / 60;
    return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
  }
  const days = m / (60 * 24);
  return Number.isInteger(days) ? `${days} days` : `${days.toFixed(1)} days`;
}
